import { useAuditLogForEntity } from "@/lib/api/audit-log";
import type { AuditLogEntry } from "@/lib/api/types";
import { ApiError } from "@/lib/api/client";

// AuditLogViewer is the generic per-entity timeline. It powers the
// audit-log panel on every entity detail page; the consumer just supplies
// `entityType` (e.g. "grow") and `entityID` (the ULID).
//
// Actions surface with `actor` (user/ai/system), the reason if provided,
// and the JSON before/after blobs collapsed by default for readability.

export interface AuditLogViewerProps {
  entityType: string;
  entityID: string;
}

export function AuditLogViewer({ entityType, entityID }: AuditLogViewerProps) {
  const q = useAuditLogForEntity({ entityType, entityID });
  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading audit trail…</p>;
  }
  if (q.error) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Failed to load audit trail: {extractError(q.error)}
      </p>
    );
  }
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No audit entries yet.</p>
    );
  }
  return (
    <ol className="space-y-3 border-l pl-4">
      {items.map((entry) => (
        <AuditLine key={entry.id} entry={entry} />
      ))}
    </ol>
  );
}

function AuditLine({ entry }: { entry: AuditLogEntry }) {
  return (
    <li className="relative">
      <span className="absolute -left-[1.4rem] top-2 size-2 rounded-full bg-foreground/40" />
      <div className="flex items-baseline gap-2 text-sm">
        <span className="font-medium">{formatAction(entry.action)}</span>
        <span className="text-xs text-muted-foreground">
          by <ActorBadge actor={entry.actor} />
        </span>
        <span className="text-xs text-muted-foreground">
          {formatTime(entry.occurred_at)}
        </span>
      </div>
      {entry.reason && (
        <p className="mt-0.5 text-xs italic text-muted-foreground">
          “{entry.reason}”
        </p>
      )}
      {Boolean(entry.before_json) || Boolean(entry.after_json) ? (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            diff
          </summary>
          <pre className="mt-1 overflow-x-auto rounded bg-muted/50 p-2 text-xs">
            {JSON.stringify(
              { before: entry.before_json, after: entry.after_json },
              null,
              2,
            )}
          </pre>
        </details>
      ) : null}
    </li>
  );
}

// Known audit verbs mapped to human-readable labels. The grow's Activity tab
// now also receives the pending-action resolution events, so those read
// cleanly ("Action completed" rather than "Pending_action_completed").
const ACTION_LABELS: Record<string, string> = {
  pending_action_completed: "Action completed",
  pending_action_skipped: "Action skipped",
  pending_action_dismissed: "Action dismissed",
  create: "Created",
  update: "Updated",
  void: "Voided",
  restore: "Restored",
  ai_recommendation_accepted: "AI recommendation accepted",
  ai_recommendation_rejected: "AI recommendation rejected",
  flag_chemistry_violation: "Chemistry violation flagged",
};

// formatAction humanizes an audit action: known verbs use a curated label;
// anything else gets underscores swapped for spaces and the first letter
// capitalized.
function formatAction(action: string): string {
  const known = ACTION_LABELS[action];
  if (known) return known;
  const spaced = action.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function ActorBadge({ actor }: { actor: AuditLogEntry["actor"] }) {
  const cls =
    actor === "ai"
      ? "bg-violet-500/10 text-violet-600 dark:text-violet-300"
      : actor === "system"
        ? "bg-slate-500/10 text-slate-600 dark:text-slate-300"
        : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300";
  return (
    <span
      className={
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase " +
        cls
      }
    >
      {actor}
    </span>
  );
}

function formatTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}
